// i18n configuration and utilities
import { en } from './locales/en';
import { zhTW } from './locales/zh-TW';
import { es } from './locales/es';

export type Language = 'en' | 'zh-TW' | 'es';

export const languages = {
  en,
  'zh-TW': zhTW,
  es,
};

export const languageNames: Record<Language, string> = {
  en: 'English',
  'zh-TW': '繁體中文',
  es: 'Español',
};

// Map Language type to Intl locale codes
const localeMap: Record<Language, string> = {
  en: 'en-US',
  'zh-TW': 'zh-TW',
  es: 'es-ES',
};

export type TranslationKeys = typeof en;

// Get translation with fallback
export function getTranslation(
  lang: Language,
  key: string
): string {
  const keys = key.split('.');
  let value: any = languages[lang];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback to English
      value = languages.en;
      for (const fallbackKey of keys) {
        if (value && typeof value === 'object' && fallbackKey in value) {
          value = value[fallbackKey];
        } else {
          return key; // Return key if not found
        }
      }
      break;
    }
  }

  return typeof value === 'string' ? value : key;
}

// Format date based on locale
export function formatDate(date: Date | string, lang: Language, format: 'short' | 'long' | 'full' = 'short'): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  const formatOptions: Record<'short' | 'long' | 'full', Intl.DateTimeFormatOptions> = {
    short: { year: 'numeric', month: '2-digit', day: '2-digit' },
    long: { year: 'numeric', month: 'long', day: 'numeric' },
    full: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
  };

  const options = formatOptions[format];
  const locale = localeMap[lang];
  return new Intl.DateTimeFormat(locale, options).format(d);
}

// Format time based on locale
export function formatTime(date: Date | string, lang: Language, includeSeconds = false): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = localeMap[lang];

  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
  };

  return new Intl.DateTimeFormat(locale, options).format(d);
}

// Format date and time
export function formatDateTime(date: Date | string, lang: Language): string {
  return `${formatDate(date, lang, 'short')} ${formatTime(date, lang)}`;
}

// Format number based on locale
export function formatNumber(num: number, lang: Language, decimals = 2): string {
  const locale = localeMap[lang];
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

// Format currency
export function formatCurrency(amount: number, lang: Language, currency = 'USD'): string {
  const locale = localeMap[lang];
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

// Format weight (kg/lb based on locale)
export function formatWeight(kg: number, lang: Language): string {
  if (lang === 'zh-TW') {
    return `${formatNumber(kg, lang)} 公斤`;
  }
  // English and Spanish both use lb
  return `${formatNumber(kg * 2.20462, lang)} lb`;
}
